import styled from '@emotion/styled';
import { useMemo } from 'react';
import { IColorFacetValue, IFacetValue } from '~/lib/data-contract';
import { Checkbox } from '~/shared/components/FormElements/components/Checkbox/Checkbox';
import { caluclateItemInRows } from '~/shared/utils/calculate-rows/calculate-rows';
import { breakpoints } from '~/theme';
import { getHexColor } from '~/theme/utils/getHexColor';

const StyledFacetGroup = styled.div(({ theme }) => ({
	'--facet-rows-sm': 8,
	'--facet-rows-md': 6,
	'--facet-rows-lg': 4,
	display: 'grid',
	gap: theme.space[4],

	[breakpoints.sm]: {
		gridAutoFlow: 'column',
		gridTemplateRows: 'repeat(var(--facet-rows-sm), auto)',
	},

	[breakpoints.md]: {
		gridTemplateRows: 'repeat(var(--facet-rows-md), auto)',
	},

	[breakpoints.lg]: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
		gridTemplateRows: 'repeat(var(--facet-rows-lg), auto)',
	},

	[breakpoints.xl]: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
		gridTemplateRows: 'repeat(var(--facet-rows-lg), auto)',
	},
}));

export type FacetSelectionProps = {
	facetValues: IFacetValue[] | IColorFacetValue[];
	onFacetValueSelected: (facetId: string) => void;
	className?: string;
	facetLoading?: string;
};

export const FacetSelection = ({ facetValues, onFacetValueSelected, className, facetLoading }: FacetSelectionProps) => {
	const rows: { sm: number; md: number; lg: number } = useMemo(() => {
		const itemsLength = facetValues.length;

		return {
			sm: caluclateItemInRows(itemsLength, 2),
			md: caluclateItemInRows(itemsLength, 3),
			lg: caluclateItemInRows(itemsLength, 4),
		};
	}, [facetValues]);

	const getFacetColor = (facetValue: IColorFacetValue) => {
		if (facetValue.customColorCode) {
			// Escape CSS closing ';' character and only use valid CSS value
			return facetValue.customColorCode.split(';')[0];
		}

		return getHexColor(facetValue.hexColorCode);
	};

	return (
		<StyledFacetGroup
			className={className}
			style={{
				['--facet-rows-sm' as string]: rows.sm,
				['--facet-rows-md' as string]: rows.md,
				['--facet-rows-lg' as string]: rows.lg,
			}}
		>
			{facetValues.map((facetValue) => (
				<Checkbox
					key={facetValue.id}
					disabled={!facetValue.count}
					label={facetValue.label}
					checked={facetValue.selected}
					suffix={facetValue.count}
					onChange={() => onFacetValueSelected(facetValue.id)}
					colorIndicator={facetValue.type === 'colorFacetValue' ? getFacetColor(facetValue) : undefined}
					isLoading={facetLoading === facetValue.id}
				/>
			))}
		</StyledFacetGroup>
	);
};
